// Hook to fetch approved reviews for a given canonical city slug
// (jabalpur / bhopal / nagpur / online / other) and produce both a Review JSON-LD
// array (Schema.org) and a list usable for rendering on the page.
//
// Reviews are injected into the LocalBusiness schema's `review` field on the
// V8/V9 priority-city pages so Google's local pack consumes real parent
// testimonials (the single largest organic local-pack lever).

import { useEffect, useState, useMemo } from "react";
import { api } from "./api";

const CACHE = new Map();

export function useApprovedReviews(citySlug, limit = 25) {
    const [reviews, setReviews] = useState(() => CACHE.get(citySlug) || []);
    const [loading, setLoading] = useState(!CACHE.has(citySlug));

    useEffect(() => {
        if (!citySlug) return undefined;
        let cancelled = false;
        const q = citySlug ? `?city=${encodeURIComponent(citySlug)}&limit=${limit}` : `?limit=${limit}`;
        api.get(`/reviews${q}`)
            .then(({ data }) => {
                if (cancelled) return;
                const list = data?.reviews || [];
                CACHE.set(citySlug, list);
                setReviews(list);
            })
            .catch(() => { if (!cancelled) setReviews([]); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [citySlug, limit]);

    const reviewSchema = useMemo(() => {
        if (!reviews || reviews.length === 0) return [];
        return reviews.slice(0, 15).map((r) => ({
            "@type": "Review",
            "reviewRating": {
                "@type": "Rating",
                "ratingValue": r.rating,
                "bestRating": 5,
                "worstRating": 1,
            },
            "author": { "@type": "Person", "name": r.parent_name || "Anand Tutorials Parent" },
            "datePublished": r.published_at || undefined,
            "name": r.review_title || undefined,
            "reviewBody": r.review_body,
        }));
    }, [reviews]);

    return { reviews, reviewSchema, loading };
}
